import {
    Button,
    Chip,
    TableCell,
    TableRow,
    Tooltip,
    useTheme,
} from '@material-ui/core'
import { Add, PostAdd, WarningRounded } from '@material-ui/icons'
import { fade } from '@material-ui/core/styles/colorManipulator'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
    CellData,
    Container,
    HeadCell,
    RenderRow,
    RowData,
    SideDrawerContainer,
    SimpleTable,
} from '../../components'
import { useAppDispatch } from '../../hooks'
import { ChangeOrderConfig } from '../../models'
import { getChangeOrderConfigListRequest, RootState } from '../../store'
import { useStyles } from '../../styles'
import { BulkChangeOrderConfigModal } from './BulkChangeOrderConfigModal'
import {
    CHANGE_ORDER_CONFIG_DRAWER_WIDTH,
    ChangeOrderConfigDrawer,
} from './ChangeOrderConfigDrawer'
import { typographyStyle } from '../../theme/typographyStyle'

export const ChangeOrderConfigList = () => {
    const dispatch = useAppDispatch()

    const changeOrderConfigList = useSelector(
        (state: RootState) => state.aptConfig.changeOrderConfigList,
    )
    const [selectedConfig, setSelectedConfig] = useState<ChangeOrderConfig>()
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [bulkModalOpen, setBulkModalOpen] = useState(false)
    const theme = useTheme()
    const classes = useStyles()

    useEffect(() => {
        dispatch(getChangeOrderConfigListRequest({}))
    }, [])

    const headCellStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.h6),
        fontWeight: 500,
    }

    interface ChangeOrderConfigRowType extends RowData {
        name: CellData<string>
        description: CellData<string>
        suggestedAddPrice: CellData<number>
        vendors: CellData<string>
    }

    const headCells: HeadCell<ChangeOrderConfigRowType>[] = [
        {
            disablePadding: false,
            align: 'left',
            id: 'name',
            label: 'Name',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'center',
            id: 'description',
            label: 'Description',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'center',
            id: 'suggestedAddPrice',
            label: 'Expense',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'center',
            id: 'vendors',
            label: 'Vendors',
            style: headCellStyle,
        },
    ]

    const createRow = (
        changeOrderConfig: ChangeOrderConfig,
    ): ChangeOrderConfigRowType => {
        const vendorCount = changeOrderConfig.vendors?.length ?? 0
        const noVendorsSelected =
            changeOrderConfig.available_to_all_vendors === false &&
            vendorCount === 0
        const vendorDisplay = noVendorsSelected
            ? 'None'
            : changeOrderConfig.available_to_all_vendors !== false
            ? 'All'
            : vendorCount === 1
            ? '1 vendor'
            : `${vendorCount} vendors`

        return {
            name: {
                value: changeOrderConfig.name,
                sortValue: changeOrderConfig.name,
            },
            description: {
                value: changeOrderConfig.description,
                sortValue: changeOrderConfig.description,
            },
            suggestedAddPrice: {
                value: changeOrderConfig.suggested_add_price,
                sortValue: changeOrderConfig.suggested_add_price,
            },
            vendors: {
                value: vendorDisplay,
                sortValue: vendorDisplay,
            },
            changeOrderConfig: { value: changeOrderConfig, sortValue: 1 },
        }
    }

    interface ChangeOrderConfigRowProps {
        row: ChangeOrderConfigRowType
    }

    const ChangeOrderConfigRow: RenderRow<ChangeOrderConfigRowProps> = (
        props,
    ) => {
        const { row } = props
        const cellStyle: React.CSSProperties = {
            ...typographyStyle(theme.typography.body2),
            fontWeight: 400,
        }
        const config = row.changeOrderConfig.value
        const noVendorsSelected =
            config.available_to_all_vendors === false &&
            (config.vendors?.length ?? 0) === 0

        return (
            <TableRow
                key={`CHANGE_ORDER_CONF_ROW_${row.changeOrderConfig.value.id}`}
                style={{
                    cursor: 'pointer',
                    backgroundColor:
                        selectedConfig?.id === row.changeOrderConfig.value.id
                            ? theme.palette.grey[300]
                            : undefined,
                }}
                className={classes.hoverGrey300}
                onClick={() => {
                    setSelectedConfig(row.changeOrderConfig.value)
                    setDrawerOpen(true)
                }}
            >
                <TableCell style={cellStyle}>{row.name.value}</TableCell>
                <TableCell align="center" style={cellStyle}>
                    {row.description.value}
                </TableCell>
                <TableCell align="center" style={cellStyle}>
                    {row.suggestedAddPrice.value}
                </TableCell>
                <TableCell align="center" style={cellStyle}>
                    {noVendorsSelected ? (
                        <Tooltip title="No vendors selected — none can use this config.">
                            <Chip
                                size="small"
                                icon={
                                    <WarningRounded
                                        style={{
                                            color: theme.palette.error.main,
                                        }}
                                    />
                                }
                                label="None"
                                style={{
                                    backgroundColor: fade(
                                        theme.palette.error.main,
                                        0.12,
                                    ),
                                    color: theme.palette.error.dark,
                                    fontWeight: 600,
                                }}
                            />
                        </Tooltip>
                    ) : (
                        row.vendors.value
                    )}
                </TableCell>
            </TableRow>
        )
    }

    return (
        <SideDrawerContainer
            open={drawerOpen}
            width={CHANGE_ORDER_CONFIG_DRAWER_WIDTH}
        >
            <Container
                direction={'column'}
                flex={1}
                style={{ height: 'calc(100vh - 104px)' }}
            >
                {/* header */}
                <Container
                    style={{
                        alignItems: 'center',
                        marginBottom: theme.spacing(1),
                    }}
                >
                    <span
                        style={{
                            ...typographyStyle(theme.typography.h6),
                            fontWeight: 700,
                            flex: 1,
                        }}
                    >
                        Change Order Config
                    </span>
                    <Button
                        variant="contained"
                        color="primary"
                        disableElevation
                        startIcon={<PostAdd />}
                        onClick={() => setBulkModalOpen(true)}
                        style={{
                            textTransform: 'none',
                            marginRight: theme.spacing(1.5),
                            fontWeight: 600,
                            borderRadius: 8,
                            padding: theme.spacing(0.75, 2),
                            boxShadow: 'none',
                        }}
                    >
                        Bulk create
                    </Button>
                    <Tooltip title="New Change Order Config">
                        <Add
                            fontSize="large"
                            onClick={() => {
                                setSelectedConfig(undefined)
                                setDrawerOpen(true)
                            }}
                            color="primary"
                            className={classes.highlightCircleOnHover}
                        />
                    </Tooltip>
                </Container>

                {/* Add a scrollable container for the table */}
                <div style={{ overflowX: 'auto' }}>
                    <SimpleTable<ChangeOrderConfigRowType>
                        rows={
                            changeOrderConfigList?.map(
                                (changeOrderConfig: ChangeOrderConfig) =>
                                    createRow(changeOrderConfig),
                            ) ?? []
                        }
                        render={(row) => <ChangeOrderConfigRow row={row} />}
                        orderByDefault={'name'}
                        headCells={headCells}
                    />
                </div>

                <ChangeOrderConfigDrawer
                    open={drawerOpen}
                    handleClose={() => {
                        setSelectedConfig(undefined)
                        setDrawerOpen(false)
                    }}
                    changeOrderConfig={selectedConfig}
                />

                <BulkChangeOrderConfigModal
                    open={bulkModalOpen}
                    handleClose={() => setBulkModalOpen(false)}
                />
            </Container>
        </SideDrawerContainer>
    )
}
